import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Stack,
  useTheme
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import SpeedIcon from '@mui/icons-material/Speed';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

export interface ImportSummaryProps {
  /**
   * Résumé de l'importation
   */
  summary: {
    totalTransactions: number;
    processedTransactions: number;
    skippedTransactions: number;
    fuelConsumptionsCreated: number;
    mileageUpdates: number;
    unknownVehiclesCount: number;
    errorsCount: number;
    // Autres statistiques optionnelles
    affectedVehicles?: string[];
    processingTime?: number;
  };
  
  /**
   * Titre du résumé
   * @default "Résumé de l'importation"
   */
  title?: string;
  
  /**
   * Indique si l'importation est en cours
   * @default false
   */
  loading?: boolean;
}

/**
 * Composant pour afficher le résumé des résultats d'importation
 */
const ImportSummary = ({
  summary,
  title = "Résumé de l'importation",
  loading = false
}: ImportSummaryProps): React.ReactElement => {
  const theme = useTheme();
  
  // Calculer le pourcentage de réussite
  const successRate = summary.totalTransactions > 0
    ? Math.round((summary.processedTransactions / summary.totalTransactions) * 100)
    : 0;
  
  // Déterminer le statut global de l'importation
  const getStatus = () => {
    if (loading) return { label: 'En cours', color: 'info', icon: <InfoIcon /> };
    if (summary.errorsCount > 0 && summary.processedTransactions === 0) return { label: 'Échec', color: 'error', icon: <ErrorIcon /> };
    if (summary.errorsCount > 0 || summary.unknownVehiclesCount > 0) return { label: 'Partielle', color: 'warning', icon: <WarningIcon /> };
    return { label: 'Complète', color: 'success', icon: <CheckCircleIcon /> };
  };
  
  const status = getStatus();
  
  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{title}</Typography>
          <Chip 
            icon={status.icon} 
            label={status.label} 
            color={status.color as 'info' | 'error' | 'warning' | 'success'} 
            variant="outlined" 
          />
        </Box>
        
        {loading && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress />
          </Box>
        )}
        
        <Grid container spacing={3}>
          {/* Statistiques principales */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Transactions
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total
                    </Typography>
                    <Typography variant="h6">
                      {summary.totalTransactions}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Traitées
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {summary.processedTransactions}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Ignorées
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      {summary.skippedTransactions}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Taux de réussite
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="h6" color={successRate > 80 ? 'success.main' : successRate > 50 ? 'warning.main' : 'error.main'}>
                        {successRate}%
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 2, mb: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={successRate} 
                    color={successRate > 80 ? 'success' : successRate > 50 ? 'warning' : 'error'} 
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Résultats détaillés */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Résultats
                </Typography>
                
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocalGasStationIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      Consommations de carburant créées
                    </Typography>
                    <Chip 
                      label={summary.fuelConsumptionsCreated} 
                      color="primary" 
                      size="small" 
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SpeedIcon sx={{ color: theme.palette.success.main, mr: 1 }} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      Mises à jour de kilométrage
                    </Typography>
                    <Chip 
                      label={summary.mileageUpdates} 
                      color="success" 
                      size="small" 
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DirectionsCarIcon sx={{ color: theme.palette.warning.main, mr: 1 }} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      Véhicules non reconnus
                    </Typography>
                    <Chip 
                      label={summary.unknownVehiclesCount} 
                      color={summary.unknownVehiclesCount > 0 ? 'warning' : 'default'} 
                      size="small" 
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ErrorIcon sx={{ color: theme.palette.error.main, mr: 1 }} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      Erreurs rencontrées
                    </Typography>
                    <Chip 
                      label={summary.errorsCount} 
                      color={summary.errorsCount > 0 ? 'error' : 'default'} 
                      size="small" 
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Informations supplémentaires */}
          {(summary.affectedVehicles?.length || summary.processingTime) && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              
              <Grid container spacing={2}>
                {summary.affectedVehicles?.length && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Véhicules concernés
                    </Typography>
                    <Typography variant="body1">
                      {summary.affectedVehicles.length} véhicule(s)
                    </Typography>
                  </Grid>
                )}
                
                {summary.processingTime && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Temps de traitement
                    </Typography>
                    <Typography variant="body1">
                      {(summary.processingTime / 1000).toFixed(2)} secondes
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default ImportSummary;